import React, { useRef, useState, useMemo } from "react";
import {
  Container,
  Button,
  Table,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCode,
  faUpload,
  faTrash,
  faUsers,
  faCheckCircle,
  faExclamationTriangle,
  faBoxes,
  faExchangeAlt,
  faArrowLeft,
  faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import { HeadPage, showToast } from "src/componentes";
import { useNavigate } from "react-router-dom";
import { SoNumeros } from "src/hook/Functions";
import {
  NFeXmlItem,
  NFeParticipanteXml,
  parseNFeXml,
} from "./CTe.ImportarXml.Dto";
import {
  CTeDto,
  CTePessoaDto,
  STORAGECTE_KEY,
  getLocal,
  updateLocal,
} from "../CTe.Dto";
import { PessoaFiltrar, PessoaIncluir } from "src/services/Entidade.Service";
import { PessoaDto } from "src/pages/Privada/Entidade/Pessoa/Pessoa.Dto";

export const CTeImportarXmlView = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [notas, setNotas] = useState<NFeXmlItem[]>([]);
  const [selectedRemetenteDoc, setSelectedRemetenteDoc] = useState<string>("");
  const [selectedDestinatarioDoc, setSelectedDestinatarioDoc] =
    useState<string>("");
  const [selectedTomadorDoc, setSelectedTomadorDoc] = useState<string>("");
  const [loadingImport, setLoadingImport] = useState(false);

  // Formatação de Documento (CNPJ/CPF)
  const formatarDoc = (doc: string) => {
    const clean = SoNumeros(doc || "");
    if (clean.length === 14) {
      return clean.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5"
      );
    }
    if (clean.length === 11) {
      return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    }
    return doc;
  };

  const formatarMoeda = (val: number) => {
    return (val || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatarPeso = (val: number) => {
    return (val || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 3,
    });
  };

  // Upload e leitura dos arquivos XML
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const novosItens: NFeXmlItem[] = [];
    const promises: Promise<void>[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const promise = new Promise<void>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          if (content) {
            const parsed = parseNFeXml(content, file.name);
            if (parsed) {
              novosItens.push(parsed);
            } else {
              showToast({
                message: `O arquivo ${file.name} não é um XML de NF-e válido.`,
                title: "Atenção",
                variant: "Warning",
              });
            }
          }
          resolve();
        };
        reader.onerror = () => resolve();
        reader.readAsText(file);
      });
      promises.push(promise);
    }

    await Promise.all(promises);

    if (novosItens.length > 0) {
      setNotas((prev) => {
        // Evitar duplicados por chave
        const existingKeys = new Set(prev.map((item) => item.chave));
        const filteredNovos = novosItens.filter(
          (item) => !item.chave || !existingKeys.has(item.chave)
        );
        return [...prev, ...filteredNovos];
      });
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Toggle seleção de NF-e individual
  const handleToggleImportarNFe = (id: string) => {
    setNotas((prev) =>
      prev.map((nota) =>
        nota.id === id
          ? {
              ...nota,
              importarNFe: !nota.importarNFe,
              importarChave: !nota.importarNFe,
            }
          : nota
      )
    );
  };

  // Toggle todos importar NFe
  const handleToggleAllNFe = (checked: boolean) => {
    setNotas((prev) =>
      prev.map((nota) => ({
        ...nota,
        importarNFe: checked,
        importarChave: checked,
      }))
    );
  };

  // Remover NF-e
  const handleRemoverNota = (id: string) => {
    setNotas((prev) => prev.filter((nota) => nota.id !== id));
  };

  // Limpar todas
  const handleLimparTodas = () => {
    setNotas([]);
    setSelectedRemetenteDoc("");
    setSelectedDestinatarioDoc("");
    setSelectedTomadorDoc("");
  };

  // Notas selecionadas para importação
  const notasSelecionadas = useMemo(() => {
    return notas.filter((n) => n.importarNFe);
  }, [notas]);

  // Totais das notas selecionadas
  const totais = useMemo(() => {
    let valorProdutos = 0;
    let valorTotalNF = 0;
    let pesoLiquido = 0;
    let pesoBruto = 0;

    notasSelecionadas.forEach((n) => {
      valorProdutos += n.valorProdutos;
      valorTotalNF += n.valorTotalNF;
      pesoLiquido += n.pesoLiquido;
      pesoBruto += n.pesoBruto;
    });

    return {
      quantidade: notasSelecionadas.length,
      valorProdutos,
      valorTotalNF,
      pesoLiquido,
      pesoBruto,
    };
  }, [notasSelecionadas]);

  // Participantes únicos extraídos das notas selecionadas
  const participantes = useMemo(() => {
    const mapa = new Map<string, NFeParticipanteXml>();

    notasSelecionadas.forEach((n) => {
      // Emitente
      if (n.emitente && n.emitente.documento) {
        const doc = n.emitente.documento;
        if (!mapa.has(doc)) {
          mapa.set(doc, {
            ...n.emitente,
            origem: "Emitente",
          });
        }
      }
      // Destinatário
      if (n.destinatario && n.destinatario.documento) {
        const doc = n.destinatario.documento;
        if (!mapa.has(doc)) {
          mapa.set(doc, {
            ...n.destinatario,
            origem: "Destinatário",
          });
        }
      }
    });

    return Array.from(mapa.values());
  }, [notasSelecionadas]);

  // Regra de Seleção Automática quando as notas selecionadas mudam:
  // Se todas as notas tiverem o mesmo emitente e mesmo destinatário, seleciona automaticamente.
  // Senão, limpa para o usuário escolher.
  useMemo(() => {
    if (notasSelecionadas.length === 0) {
      setSelectedRemetenteDoc("");
      setSelectedDestinatarioDoc("");
      setSelectedTomadorDoc("");
      return;
    }

    const emitentesUnicos = Array.from(
      new Set(
        notasSelecionadas.map((n) => n.emitente.documento).filter(Boolean)
      )
    );
    const destinatariosUnicos = Array.from(
      new Set(
        notasSelecionadas.map((n) => n.destinatario.documento).filter(Boolean)
      )
    );

    const mesmoRemetente = emitentesUnicos.length === 1;
    const mesmoDestinatario = destinatariosUnicos.length === 1;

    if (mesmoRemetente && mesmoDestinatario) {
      const remetenteAuto = emitentesUnicos[0];
      const destinatarioAuto = destinatariosUnicos[0];

      setSelectedRemetenteDoc(remetenteAuto);
      setSelectedDestinatarioDoc(destinatarioAuto);

      setSelectedTomadorDoc((prev) => {
        if (!prev || (prev !== remetenteAuto && prev !== destinatarioAuto)) {
          return remetenteAuto;
        }
        return prev;
      });
    } else {
      setSelectedRemetenteDoc("");
      setSelectedDestinatarioDoc("");
      setSelectedTomadorDoc("");
    }
  }, [notasSelecionadas]);

  // Determinar o Tipo Tomador com base na pessoa selecionada como Tomador
  // 0 = Remetente, 3 = Destinatário, 4 = Outros
  const tipoTomadorCalculado = useMemo(() => {
    if (!selectedTomadorDoc) return null;
    if (selectedTomadorDoc === selectedRemetenteDoc) {
      return 0; // Remetente
    }
    if (selectedTomadorDoc === selectedDestinatarioDoc) {
      return 3; // Destinatário
    }
    return 4; // Outros
  }, [selectedTomadorDoc, selectedRemetenteDoc, selectedDestinatarioDoc]);

  // Manipulador quando usuário clica em Tomador na linha de participante
  const handleSelectTomador = (doc: string) => {
    setSelectedTomadorDoc(doc);
  };

  // Garantir inclusão automática da pessoa na API de Entidade se não existir
  const garantirPessoaCadastrada = async (
    p: CTePessoaDto,
    perfilPessoaPadrao: number = 3
  ): Promise<{ id?: number; incluido: boolean }> => {
    const docLimpo = SoNumeros(p.documento || "");
    if (!docLimpo) return { id: undefined, incluido: false };

    // 1. Buscar pessoa no banco de dados da API de Entidade
    try {
      const resBusca = await PessoaFiltrar({
        documento: docLimpo,
        perfilPessoa: 0,
      });

      if (resBusca.data?.success && Array.isArray(resBusca.data?.data)) {
        const encontrada =
          resBusca.data.data.find(
            (item: PessoaDto) => SoNumeros(item.documento) === docLimpo
          ) || resBusca.data.data[0];

        if (encontrada && encontrada.id) {
          return { id: encontrada.id, incluido: false };
        }
      }
    } catch (err) {
      console.warn("Aviso ao buscar pessoa por documento:", docLimpo, err);
    }

    // 2. Se não existir, incluir automaticamente na base de dados
    const nomeLimpo = (p.nome || "").trim().substring(0, 60);
    const fantasiaLimpa = (p.nomeFantasia || p.nome || "").trim().substring(0, 60);
    const cepLimpo = SoNumeros(p.cep || "");
    const enderecoLimpo = (p.endereco || "NÃO INFORMADO").trim().substring(0, 60);
    const numeroLimpo = (p.enderecoNumero || "S/N").trim().substring(0, 60);
    const bairroLimpo = (p.bairro || "CENTRO").trim().substring(0, 60);
    const codigoMunicipioLimpo = p.codigoMunicipio
      ? String(p.codigoMunicipio).padStart(7, "0")
      : "";
    const municipioLimpo = (p.nomeMunicipio || "").trim().substring(0, 60);
    const ufLimpa = (p.uf || "").trim().toUpperCase().substring(0, 2);
    const ieLimpa = (p.inscricaoEstadual || "").trim();

    let indicadorIE = 9; // Não Contribuinte
    if (ieLimpa && ieLimpa.toUpperCase() === "ISENTO") {
      indicadorIE = 2; // Contribuinte Isento
    } else if (ieLimpa && SoNumeros(ieLimpa).length > 0) {
      indicadorIE = 1; // Contribuinte
    } else if (p.indicadorIETomador) {
      indicadorIE = p.indicadorIETomador;
    }

    const apelidoLimpo = (fantasiaLimpa || nomeLimpo || "EMPRESA")
      .split(" ")[0]
      .trim()
      .substring(0, 10);

    const novaPessoa: PessoaDto = {
      id: 0,
      documento: docLimpo,
      nome: nomeLimpo.length >= 3 ? nomeLimpo : `${nomeLimpo} - EMPRESA`,
      nomeFantasia: fantasiaLimpa.length >= 3 ? fantasiaLimpa : nomeLimpo,
      apelido: apelidoLimpo.length >= 3 ? apelidoLimpo : "EMPRESA",
      endereco:
        enderecoLimpo.length >= 3
          ? enderecoLimpo
          : "ENDEREÇO NÃO INFORMADO",
      enderecoNumero: numeroLimpo,
      enderecoComplemento: "",
      bairro: bairroLimpo.length >= 3 ? bairroLimpo : "CENTRO",
      codigoMunicipio: codigoMunicipioLimpo,
      nomeMunicipio:
        municipioLimpo.length >= 3 ? municipioLimpo : "MUNICIPIO",
      uf: ufLimpa,
      cep: cepLimpo,
      codigoPais: p.codigoPais || "1058",
      nomePais: p.nomePais || "BRASIL",
      inscricaoEstadual: ieLimpa,
      indicadorInscricaoEstadual: indicadorIE,
      perfilPessoa: perfilPessoaPadrao,
    };

    try {
      const resIncluir = await PessoaIncluir(novaPessoa);
      if (resIncluir.data?.success && resIncluir.data?.data?.id) {
        return { id: resIncluir.data.data.id, incluido: true };
      }
    } catch (err: any) {
      console.warn("Aviso ao incluir pessoa automaticamente:", novaPessoa, err);
    }

    return { id: undefined, incluido: false };
  };

  // Confirmar Importação e Preencher CT-e no LocalStorage
  const handleConfirmarImportacao = async () => {
    if (notasSelecionadas.length === 0) {
      showToast({
        message: "Selecione ao menos uma NF-e para importar.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }

    if (!selectedRemetenteDoc) {
      showToast({
        message:
          "Selecione um Remetente na lista de participantes.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }

    if (!selectedDestinatarioDoc) {
      showToast({
        message:
          "Selecione um Destinatário na lista de participantes.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }

    if (!selectedTomadorDoc) {
      showToast({
        message:
          "Selecione um Tomador na lista de participantes.",
        title: "Atenção",
        variant: "Warning",
      });
      return;
    }

    const remetenteObj = participantes.find(
      (p) => p.documento === selectedRemetenteDoc
    );
    const destinatarioObj = participantes.find(
      (p) => p.documento === selectedDestinatarioDoc
    );
    const tomadorObj = participantes.find(
      (p) => p.documento === selectedTomadorDoc
    );

    if (!remetenteObj || !destinatarioObj || !tomadorObj) {
      showToast({
        message: "Erro ao localizar os dados dos participantes selecionados.",
        title: "Erro",
        variant: "Danger",
      });
      return;
    }

    setLoadingImport(true);
    try {
      let novasInclusoes = 0;

      // 1. Garantir Remetente no banco da API
      const resRem = await garantirPessoaCadastrada(remetenteObj, 3);
      if (resRem.id) {
        remetenteObj.id = resRem.id;
      }
      if (resRem.incluido) {
        novasInclusoes++;
      }

      // 2. Garantir Destinatário no banco da API
      const resDest = await garantirPessoaCadastrada(destinatarioObj, 3);
      if (resDest.id) {
        destinatarioObj.id = resDest.id;
      }
      if (resDest.incluido) {
        novasInclusoes++;
      }

      // 3. Garantir Tomador no banco da API se for 'Outros'
      const tipoTomador = tipoTomadorCalculado ?? 0;
      if (tipoTomador === 4 && tomadorObj) {
        const resTom = await garantirPessoaCadastrada(tomadorObj, 3);
        if (resTom.id) {
          tomadorObj.id = resTom.id;
        }
        if (resTom.incluido) {
          novasInclusoes++;
        }
      }

      // Montar documentos transportados (chaves de acesso de 44 dígitos das notas selecionadas)
      const documentosImportados = notasSelecionadas
        .filter((n) => n.chave && n.chave.length === 44)
        .map((n) => ({
          tipoDocumento: 1, // NF-e
          chaveDocumento: n.chave,
        }));

      // Produto predominante (tenta espécie de volume ou padrão)
      const produtoPredominante =
        notasSelecionadas.find((n) => n.especieVolumes)?.especieVolumes ||
        "MERCADORIAS DIVERSAS";

      // Montar quantidades de carga
      const quantidades = [];
      if (totais.pesoBruto > 0) {
        quantidades.push({
          codigoUnidadeMedida: "01", // KG
          tipoMedida: "PESO BRUTO",
          quantidadeCarga: totais.pesoBruto,
        });
      }
      if (totais.pesoLiquido > 0) {
        quantidades.push({
          codigoUnidadeMedida: "01", // KG
          tipoMedida: "PESO LIQUIDO",
          quantidadeCarga: totais.pesoLiquido,
        });
      }
      if (quantidades.length === 0) {
        quantidades.push({
          codigoUnidadeMedida: "01",
          tipoMedida: "PESO BRUTO",
          quantidadeCarga: 0,
        });
      }

      // Recuperar CT-e salvo para manter série se houver
      const cteAtual = getLocal<CTeDto>(STORAGECTE_KEY);

      const ctePreenchido: Partial<CTeDto> = {
        ...(cteAtual || {}),
        serie: cteAtual?.serie || 1,
        numeroCte: 0,
        tipoCte: 0, // Normal
        tipoServico: 0, // Normal
        finalidadeCte: 0, // Normal
        modal: 1, // Rodoviário
        retira: 1, // Não
        tipoTomador: tipoTomador,
        // Percurso de início (Remetente)
        codigoMunicipioInicio: Number(remetenteObj.codigoMunicipio) || 0,
        nomeMunicipioInicio: remetenteObj.nomeMunicipio || "",
        ufInicio: remetenteObj.uf || "",
        // Percurso de término (Destinatário)
        codigoMunicipioFim: Number(destinatarioObj.codigoMunicipio) || 0,
        nomeMunicipioFim: destinatarioObj.nomeMunicipio || "",
        ufFim: destinatarioObj.uf || "",
        // Remetente
        remetente: {
          id: remetenteObj.id,
          documento: remetenteObj.documento,
          nome: remetenteObj.nome,
          nomeFantasia: remetenteObj.nomeFantasia || remetenteObj.nome,
          inscricaoEstadual: remetenteObj.inscricaoEstadual || "",
          fone: remetenteObj.fone || "",
          endereco: remetenteObj.endereco || "",
          enderecoNumero: remetenteObj.enderecoNumero || "",
          bairro: remetenteObj.bairro || "",
          codigoMunicipio: remetenteObj.codigoMunicipio || 0,
          nomeMunicipio: remetenteObj.nomeMunicipio || "",
          uf: remetenteObj.uf || "",
          cep: remetenteObj.cep || "",
          codigoPais: remetenteObj.codigoPais || "1058",
          nomePais: remetenteObj.nomePais || "BRASIL",
        },
        // Destinatário
        destinatario: {
          id: destinatarioObj.id,
          documento: destinatarioObj.documento,
          nome: destinatarioObj.nome,
          nomeFantasia: destinatarioObj.nomeFantasia || destinatarioObj.nome,
          inscricaoEstadual: destinatarioObj.inscricaoEstadual || "",
          indicadorIETomador: destinatarioObj.indicadorIETomador || 1,
          fone: destinatarioObj.fone || "",
          endereco: destinatarioObj.endereco || "",
          enderecoNumero: destinatarioObj.enderecoNumero || "",
          bairro: destinatarioObj.bairro || "",
          codigoMunicipio: destinatarioObj.codigoMunicipio || 0,
          nomeMunicipio: destinatarioObj.nomeMunicipio || "",
          uf: destinatarioObj.uf || "",
          cep: destinatarioObj.cep || "",
          codigoPais: destinatarioObj.codigoPais || "1058",
          nomePais: destinatarioObj.nomePais || "BRASIL",
        },
        // Tomador quando "Outros"
        tomador:
          tipoTomador === 4
            ? {
                id: tomadorObj.id,
                documento: tomadorObj.documento,
                nome: tomadorObj.nome,
                nomeFantasia: tomadorObj.nomeFantasia || tomadorObj.nome,
                inscricaoEstadual: tomadorObj.inscricaoEstadual || "",
                indicadorIETomador: tomadorObj.indicadorIETomador || 1,
                fone: tomadorObj.fone || "",
                endereco: tomadorObj.endereco || "",
                enderecoNumero: tomadorObj.enderecoNumero || "",
                bairro: tomadorObj.bairro || "",
                codigoMunicipio: tomadorObj.codigoMunicipio || 0,
                nomeMunicipio: tomadorObj.nomeMunicipio || "",
                uf: tomadorObj.uf || "",
                cep: tomadorObj.cep || "",
                codigoPais: tomadorObj.codigoPais || "1058",
                nomePais: tomadorObj.nomePais || "BRASIL",
              }
            : undefined,
        // Carga
        carga: {
          valorCarga: totais.valorTotalNF,
          produtoPredominante,
          outraCaracteristica: "",
          quantidades,
        },
        // Documentos
        documentos: documentosImportados,
      };

      updateLocal<CTeDto>(STORAGECTE_KEY, ctePreenchido as CTeDto);

      if (novasInclusoes > 0) {
        showToast({
          message: `${notasSelecionadas.length} NF-e(s) importada(s) e ${novasInclusoes} participante(s) cadastrado(s) automaticamente na base de dados!`,
          title: "Sucesso",
          variant: "Success",
        });
      } else {
        showToast({
          message: `${notasSelecionadas.length} NF-e(s) importada(s) com sucesso para o CT-e!`,
          title: "Sucesso",
          variant: "Success",
        });
      }

      navigate("/fiscal/cte/cadastro");
    } finally {
      setLoadingImport(false);
    }
  };

  const todosImportarNFe =
    notas.length > 0 && notas.every((n) => n.importarNFe);

  return (
    <Container className="py-3">
      <HeadPage
        headText="Importar XML da NF-e"
        onBack={() => navigate("/fiscal/cte")}
      />

      {/* Seletor de Arquivos Oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept=".xml"
        style={{ display: "none" }}
      />

      {/* Estado Vazio: Nenhum XML carregado */}
      {notas.length === 0 ? (
        <div className="text-center py-5">
          <div className="p-4 d-inline-block rounded-circle bg-light border mb-3">
            <FontAwesomeIcon icon={faFileCode} size="3x" className="text-primary" />
          </div>
          <h4>Nenhum XML de NF-e Carregado</h4>
          <p className="text-muted mb-4" style={{ maxWidth: "500px", margin: "0 auto" }}>
            Selecione um ou múltiplos arquivos XML de NF-e para extrair automaticamente os dados do Remetente, Destinatário, chaves de acesso, pesos e valores da carga para o novo CT-e.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/fiscal/cte")}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Selecionar Arquivos XML...
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Barra de Ações Superior */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 agiliza-panel-sm">
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Adicionar mais XMLs...
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleLimparTodas}
              >
                <FontAwesomeIcon icon={faTrash} className="me-1" />
                Limpar Todos ({notas.length})
              </Button>
            </div>

            <div className="text-muted small">
              <strong>{notas.length}</strong> arquivo(s) carregado(s) |{" "}
              <strong>{notasSelecionadas.length}</strong> selecionada(s) para importação
            </div>
          </div>

          {/* PARTE 1: Grid de NF-es */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-primary fw-bold mb-0">
                <FontAwesomeIcon icon={faBoxes} className="me-2" />
                1. Notas Fiscais Eletrônicas Carregadas
              </h6>
              <Badge bg="secondary">
                {notasSelecionadas.length} de {notas.length} selecionada(s)
              </Badge>
            </div>

            <div className="table-responsive border rounded mb-3">
              <Table hover size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "45px" }} className="text-center">
                      <Form.Check
                        type="checkbox"
                        title="Selecionar/Desmarcar todas as NF-es"
                        checked={todosImportarNFe}
                        onChange={(e) => handleToggleAllNFe(e.target.checked)}
                      />
                    </th>
                    <th>NF / Série</th>
                    <th>Emitente (Origem)</th>
                    <th>Destinatário (Destino)</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">Total NF</th>
                    <th className="text-end">Peso Líq.</th>
                    <th className="text-end">Peso Bruto</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {notas.map((nota) => (
                    <tr
                      key={nota.id}
                      className={!nota.importarNFe ? "opacity-50" : ""}
                    >
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={nota.importarNFe}
                          onChange={() => handleToggleImportarNFe(nota.id)}
                          title="Importar esta NF-e (valores, peso e chave de acesso)"
                        />
                      </td>
                      <td className="text-nowrap">
                        <strong>
                          {nota.numero}-{nota.serie}
                        </strong>
                      </td>
                      <td>
                        <div
                          className="fw-semibold text-truncate"
                          style={{ maxWidth: "260px" }}
                          title={
                            nota.chave
                              ? `${nota.emitente.nome}\nCNPJ/CPF: ${formatarDoc(nota.emitente.documento)}\nChave: ${nota.chave}`
                              : `${nota.emitente.nome}\nCNPJ/CPF: ${formatarDoc(nota.emitente.documento)}`
                          }
                        >
                          {nota.emitente.nome}
                        </div>
                        <small
                          className="text-muted text-truncate d-block"
                          style={{ maxWidth: "260px" }}
                        >
                          {formatarDoc(nota.emitente.documento)} -{" "}
                          {nota.emitente.nomeMunicipio} / {nota.emitente.uf}
                        </small>
                      </td>
                      <td>
                        <div
                          className="fw-semibold text-truncate"
                          style={{ maxWidth: "260px" }}
                          title={`${nota.destinatario.nome}\nCNPJ/CPF: ${formatarDoc(nota.destinatario.documento)}`}
                        >
                          {nota.destinatario.nome}
                        </div>
                        <small
                          className="text-muted text-truncate d-block"
                          style={{ maxWidth: "260px" }}
                        >
                          {formatarDoc(nota.destinatario.documento)} -{" "}
                          {nota.destinatario.nomeMunicipio} /{" "}
                          {nota.destinatario.uf}
                        </small>
                      </td>
                      <td className="text-end text-nowrap">
                        {formatarMoeda(nota.valorProdutos)}
                      </td>
                      <td className="text-end text-nowrap fw-bold text-success">
                        {formatarMoeda(nota.valorTotalNF)}
                      </td>
                      <td className="text-end text-nowrap">
                        {formatarPeso(nota.pesoLiquido)} kg
                      </td>
                      <td className="text-end text-nowrap">
                        {formatarPeso(nota.pesoBruto)} kg
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="p-1 line-height-1"
                          onClick={() => handleRemoverNota(nota.id)}
                          title="Remover este XML"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Cards de Totais Agregados */}
            <Row className="g-2 mb-4">
              <Col xs={6} md={3}>
                <Card className="agiliza-card-stat text-center">
                  <small className="text-muted">Notas Selecionadas</small>
                  <h5 className="mb-0 fw-bold">{totais.quantidade}</h5>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="agiliza-card-stat text-center">
                  <small className="text-muted">Valor da Carga (Total)</small>
                  <h5 className="mb-0 fw-bold text-success">
                    {formatarMoeda(totais.valorTotalNF)}
                  </h5>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="agiliza-card-stat text-center">
                  <small className="text-muted">Peso Bruto Total</small>
                  <h5 className="mb-0 fw-bold">
                    {formatarPeso(totais.pesoBruto)} kg
                  </h5>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="agiliza-card-stat text-center">
                  <small className="text-muted">Peso Líquido Total</small>
                  <h5 className="mb-0 fw-bold">
                    {formatarPeso(totais.pesoLiquido)} kg
                  </h5>
                </Card>
              </Col>
            </Row>
          </div>

          <hr className="my-4" />

          {/* PARTE 2: Grid de Participantes (Remetente, Destinatário, Tomador) */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="text-primary fw-bold mb-0">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  2. Participantes do Transporte
                </h6>
                <small className="text-muted">
                  Defina o papel de cada participante no CT-e (Remetente,
                  Destinatário e Tomador do Serviço).
                </small>
              </div>
              {selectedRemetenteDoc && selectedDestinatarioDoc && (
                <Badge bg="success">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                  Participantes Definidos
                </Badge>
              )}
            </div>

            {participantes.length === 0 ? (
              <Alert variant="warning" className="text-center py-3">
                Selecione ao menos uma NF-e no grid superior para listar os
                participantes.
              </Alert>
            ) : (
              <>
                {/* Alerta caso haja múltiplos remetentes ou destinatários nas notas */}
                {(!selectedRemetenteDoc || !selectedDestinatarioDoc) && (
                  <Alert variant="info" className="py-2 small">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-2 text-warning"
                    />
                    As notas selecionadas possuem diferentes remetentes ou
                    destinatários. Selecione manualmente o Remetente e o
                    Destinatário desejados abaixo.
                  </Alert>
                )}

                <div className="table-responsive border rounded mb-3">
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Documento (CNPJ/CPF)</th>
                        <th>Razão Social / Nome</th>
                        <th>Município / UF</th>
                        <th className="text-center" style={{ width: "130px" }}>
                          Remetente
                        </th>
                        <th className="text-center" style={{ width: "130px" }}>
                          Destinatário
                        </th>
                        <th className="text-center" style={{ width: "130px" }}>
                          Tomador
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {participantes.map((p) => {
                        const isRemetente = selectedRemetenteDoc === p.documento;
                        const isDestinatario =
                          selectedDestinatarioDoc === p.documento;
                        const isTomador = selectedTomadorDoc === p.documento;

                        return (
                          <tr
                            key={p.documento}
                            className={
                              isRemetente || isDestinatario || isTomador
                                ? "table-active"
                                : ""
                            }
                          >
                            <td className="fw-semibold">
                              {formatarDoc(p.documento)}
                            </td>
                            <td>
                              <div className="fw-bold">{p.nome}</div>
                              {p.endereco && (
                                <small className="text-muted d-block">
                                  {p.endereco}, {p.enderecoNumero} - {p.bairro}
                                </small>
                              )}
                            </td>
                            <td>
                              {p.nomeMunicipio} - {p.uf}
                            </td>
                            {/* Coluna Remetente */}
                            <td className="text-center">
                              <Form.Check
                                type="radio"
                                id={`remetente-${p.documento}`}
                                name="selectedRemetente"
                                checked={isRemetente}
                                onChange={() => {
                                  setSelectedRemetenteDoc(p.documento);
                                  // Se o tomador era o remetente anterior, atualiza para o novo remetente
                                  if (
                                    selectedTomadorDoc === selectedRemetenteDoc ||
                                    !selectedTomadorDoc
                                  ) {
                                    setSelectedTomadorDoc(p.documento);
                                  }
                                }}
                                label={
                                  isRemetente ? (
                                    <span className="badge bg-success">Remetente</span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </td>
                            {/* Coluna Destinatário */}
                            <td className="text-center">
                              <Form.Check
                                type="radio"
                                id={`destinatario-${p.documento}`}
                                name="selectedDestinatario"
                                checked={isDestinatario}
                                onChange={() => {
                                  setSelectedDestinatarioDoc(p.documento);
                                }}
                                label={
                                  isDestinatario ? (
                                    <span className="badge bg-success">
                                      Destinatário
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </td>
                            {/* Coluna Tomador */}
                            <td className="text-center">
                              <Form.Check
                                type="radio"
                                id={`tomador-${p.documento}`}
                                name="selectedTomador"
                                checked={isTomador}
                                onChange={() => handleSelectTomador(p.documento)}
                                label={
                                  isTomador ? (
                                    <span className="badge bg-primary">Tomador</span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Status do Tomador Resolvido e Percurso */}
                <div className="agiliza-panel d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold">Tomador do Serviço (Frete):</span>
                    {tipoTomadorCalculado === 0 && (
                      <Badge bg="success" className="p-2">
                        0 - Remetente (Tomador é o próprio Remetente)
                      </Badge>
                    )}
                    {tipoTomadorCalculado === 3 && (
                      <Badge bg="success" className="p-2">
                        3 - Destinatário (Tomador é o próprio Destinatário)
                      </Badge>
                    )}
                    {tipoTomadorCalculado === 4 && (
                      <Badge bg="warning" text="dark" className="p-2">
                        4 - Outros (Tomador é um terceiro / outro participante)
                      </Badge>
                    )}
                    {tipoTomadorCalculado === null && (
                      <Badge bg="danger" className="p-2">
                        Não selecionado
                      </Badge>
                    )}
                  </div>

                  {selectedRemetenteDoc && selectedDestinatarioDoc && (
                    <div className="text-muted small">
                      Percurso estimado:{" "}
                      <strong>
                        {
                          participantes.find(
                            (p) => p.documento === selectedRemetenteDoc
                          )?.nomeMunicipio
                        }{" "}
                        (
                        {
                          participantes.find(
                            (p) => p.documento === selectedRemetenteDoc
                          )?.uf
                        }
                        )
                      </strong>{" "}
                      <FontAwesomeIcon
                        icon={faExchangeAlt}
                        className="mx-1 text-primary"
                      />{" "}
                      <strong>
                        {
                          participantes.find(
                            (p) => p.documento === selectedDestinatarioDoc
                          )?.nomeMunicipio
                        }{" "}
                        (
                        {
                          participantes.find(
                            (p) => p.documento === selectedDestinatarioDoc
                          )?.uf
                        }
                        )
                      </strong>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Barra de Ações Inferior */}
          <div className="d-flex justify-content-between align-items-center pt-3 border-top mb-5">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/fiscal/cte")}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar para Listagem
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirmarImportacao}
              disabled={
                loadingImport ||
                notasSelecionadas.length === 0 ||
                !selectedRemetenteDoc ||
                !selectedDestinatarioDoc ||
                !selectedTomadorDoc
              }
            >
              {loadingImport ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Sincronizando participantes e importando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileImport} className="me-2" />
                  Importar e Iniciar CT-e ({notasSelecionadas.length} nota(s))
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default CTeImportarXmlView;
